import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { TransactionLogger } from '../../utils/transactionLogger';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  GolfCourse,
  CloudDownload
} from '@mui/icons-material';
import GolfCourseAPIIntegration from './GolfCourseAPIIntegration';

export default function GolfCourseList() {
  const { userProfile, currentUser, loading: authLoading } = useAuth();
  const [golfCourses, setGolfCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAPIDialog, setOpenAPIDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    holes: Array(18).fill().map((_, index) => ({
      holeNumber: index + 1,
      par: 4,
      index: index + 1
    }))
  });

  const fetchGolfCourses = async () => {
    try {
      const coursesQuery = query(
        collection(db, 'golfCourses'),
        where('organizationId', '==', userProfile?.organizationName)
      );
      const snapshot = await getDocs(coursesQuery);
      const coursesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGolfCourses(coursesData);
    } catch (error) {
      console.error('Error fetching golf courses:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.organizationName) {
      fetchGolfCourses();
    }
  }, [userProfile?.organizationName]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
  // Validate required fields
    if (!formData.name.trim()) {
      alert('Please enter a golf course name');
      return;
    }
  if (!formData.address.trim()) {
    alert('Please enter an address');
    return;
  }
  if (!formData.city.trim()) {
    alert('Please enter a city');
    return;
  }
  if (!formData.state.trim()) {
    alert('Please enter a state');
    return;
  }
  if (!formData.country.trim()) {
    alert('Please enter a country');
    return;
  }
    if (!userProfile?.organizationName) {
      alert('User profile not loaded. Please try again.');
      return;
    }
    
    try {
      const courseData = {
        ...formData,
        organizationId: userProfile?.organizationName,
        createdAt: new Date(),
        totalPar: formData.holes.reduce((sum, hole) => sum + hole.par, 0)
      };

      if (editingCourse) {
        // Log the update transaction
        await TransactionLogger.logTransaction(
          'UPDATE',
          'GOLF_COURSE',
          editingCourse.id,
          editingCourse,
          courseData,
          currentUser.uid,
          userProfile?.email
        );
        
        await updateDoc(doc(db, 'golfCourses', editingCourse.id), courseData);
        setGolfCourses(golfCourses.map(c => c.id === editingCourse.id ? { ...c, ...courseData } : c));
      } else {
        const docRef = await addDoc(collection(db, 'golfCourses'), courseData);
        
        // Log the creation transaction
        await TransactionLogger.logTransaction(
          'CREATE',
          'GOLF_COURSE',
          docRef.id,
          null,
          courseData,
          currentUser.uid,
          userProfile?.email
        );
        
        setGolfCourses([...golfCourses, { id: docRef.id, ...courseData }]);
      }

      handleCloseDialog();
    } catch (error) {
      console.error('Error saving golf course:', error);
      alert('Error saving golf course: ' + error.message);
    }
  };

  const handleDelete = async (courseId) => {
    if (window.confirm('Are you sure you want to delete this golf course?')) {
      try {
        // Get the course data before deleting for logging
        const courseToDelete = golfCourses.find(c => c.id === courseId);
        
        // Log the deletion transaction
        await TransactionLogger.logTransaction(
          'DELETE',
          'GOLF_COURSE',
          courseId,
          courseToDelete,
          null,
          currentUser.uid,
          userProfile?.email
        );
        
        await deleteDoc(doc(db, 'golfCourses', courseId));
        setGolfCourses(golfCourses.filter(c => c.id !== courseId));
      } catch (error) {
        console.error('Error deleting golf course:', error);
      }
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      name: course.name,
      address: course.address || '',
      city: course.city || '',
      state: course.state || '',
      country: course.country || '',
      holes: course.holes || Array(18).fill().map((_, index) => ({
        holeNumber: index + 1,
        par: 4,
        index: index + 1
      }))
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCourse(null);
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      country: '',
      holes: Array(18).fill().map((_, index) => ({
        holeNumber: index + 1,
        par: 4,
        index: index + 1
      }))
    });
  };

  const handleAPICourseSelected = (apiCourse) => {
    if (apiCourse) {
      // Pre-fill the form with API data
      setFormData({
        name: apiCourse.name,
        address: apiCourse.address,
        city: apiCourse.city,
        state: apiCourse.state,
        country: apiCourse.country,
        holes: apiCourse.holes || Array(18).fill().map((_, index) => ({
          holeNumber: index + 1,
          par: 4,
          index: index + 1
        }))
      });
    } else {
      // Manual course creation - reset form to empty
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        country: '',
        holes: Array(18).fill().map((_, index) => ({
          holeNumber: index + 1,
          par: 4,
          index: index + 1
        }))
      });
    }
    setOpenAPIDialog(false);
    setOpenDialog(true);
  };

  const updateHole = (holeIndex, field, value) => {
    const updatedHoles = [...formData.holes];
    updatedHoles[holeIndex] = {
      ...updatedHoles[holeIndex],
      [field]: field === 'par' || field === 'index' ? parseInt(value) || 0 : value
    };
    setFormData({ ...formData, holes: updatedHoles });
  };

  if (authLoading || loading) {
    return <Container><Typography>Loading golf courses...</Typography></Container>;
  }

  if (!currentUser) {
    return (
      <Container>
        <Typography variant="h5" color="error">
          Please log in to access golf courses.
        </Typography>
      </Container>
    );
  }

  if (!userProfile) {
    return (
      <Container>
        <Typography variant="h5" color="error">
          User profile not loaded. Please refresh the page or log in again.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Golf Courses</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<CloudDownload />}
            onClick={() => setOpenAPIDialog(true)}
          >
            Import from API
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
          >
            Add Golf Course
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {golfCourses.map((course) => (
          <Grid item xs={12} md={6} lg={4} key={course.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" component="h2">
                    {course.name}
                  </Typography>
                  <Chip 
                    label={`Par ${course.totalPar || 72}`}
                    color="primary"
                    size="small"
                  />
                </Box>
                
                <Typography color="text.secondary" gutterBottom>
                  📍 {course.city && course.state && course.country
                    ? `${course.city}, ${course.state}, ${course.country}`
                    : (course.location || '')}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  🏌️ {course.holes?.length || 18} holes
                </Typography>
              </CardContent>
              
              <CardActions>
                <Button
                  size="small"
                  startIcon={<Edit />}
                  onClick={() => handleEdit(course)}
                >
                  Edit
                </Button>
                
                <IconButton
                  size="small"
                  onClick={() => handleDelete(course.id)}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Golf Course Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCourse ? 'Edit Golf Course' : 'Add New Golf Course'}
        </DialogTitle>
        
        <DialogActions sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            form="golf-course-form"
          >
            {editingCourse ? 'Update' : 'Add'} Golf Course
          </Button>
        </DialogActions>
        
        <form id="golf-course-form" onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Golf Course Name"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
            
            <TextField
              margin="dense"
              label="Address"
              fullWidth
              variant="outlined"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              required
            />
            
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  label="City"
                  fullWidth
                  variant="outlined"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  margin="dense"
                  label="State"
                  fullWidth
                  variant="outlined"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  margin="dense"
                  label="Country"
                  fullWidth
                  variant="outlined"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  required
                />
              </Grid>
            </Grid>

            {/* Live Par Calculations */}
            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Par Calculations
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Front Nine (Holes 1-9)
                  </Typography>
                  <Typography variant="h6" color="primary">
                    Par {formData.holes.slice(0, 9).reduce((sum, hole) => sum + hole.par, 0)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Back Nine (Holes 10-18)
                  </Typography>
                  <Typography variant="h6" color="primary">
                    Par {formData.holes.slice(9, 18).reduce((sum, hole) => sum + hole.par, 0)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Course
                  </Typography>
                  <Typography variant="h6" color="secondary">
                    Par {formData.holes.reduce((sum, hole) => sum + hole.par, 0)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              Course Layout (18 Holes)
            </Typography>
            
            <Grid container spacing={2}>
              {/* Front Nine */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                  Front Nine (Holes 1-9)
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Hole</TableCell>
                        <TableCell>Par</TableCell>
                        <TableCell>Index</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.holes.slice(0, 9).map((hole, index) => (
                        <TableRow key={hole.holeNumber}>
                          <TableCell sx={{ fontWeight: 'bold' }}>{hole.holeNumber}</TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={hole.par}
                              onChange={(e) => updateHole(index, 'par', e.target.value)}
                              inputProps={{ min: 3, max: 6 }}
                              sx={{ width: 70 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={hole.index}
                              onChange={(e) => updateHole(index, 'index', e.target.value)}
                              inputProps={{ min: 1, max: 18 }}
                              sx={{ width: 70 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              {/* Back Nine */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'secondary.main' }}>
                  Back Nine (Holes 10-18)
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Hole</TableCell>
                        <TableCell>Par</TableCell>
                        <TableCell>Index</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.holes.slice(9, 18).map((hole, index) => (
                        <TableRow key={hole.holeNumber}>
                          <TableCell sx={{ fontWeight: 'bold' }}>{hole.holeNumber}</TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={hole.par}
                              onChange={(e) => updateHole(index + 9, 'par', e.target.value)}
                              inputProps={{ min: 3, max: 6 }}
                              sx={{ width: 70 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={hole.index}
                              onChange={(e) => updateHole(index + 9, 'index', e.target.value)}
                              inputProps={{ min: 1, max: 18 }}
                              sx={{ width: 70 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </DialogContent>
        </form>
      </Dialog>

      {/* API Integration Dialog */}
      {openAPIDialog && (
        <GolfCourseAPIIntegration
          onCourseSelected={handleAPICourseSelected}
          onClose={() => setOpenAPIDialog(false)}
        />
      )}
    </Container>
  );
}
